import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { handleAuthError } from "@/utilities/auth-error-handler"
import { passwordValidationRule, requiredRule } from "@/utilities/form"

export const Route = createFileRoute("/_authenticated/account/update-password")(
  {
    staticData: {
      pageName: "Update Password",
    },
    component: RouteComponent,
  }
)

interface UpdatePasswordFormSchema {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

// Helper function to log mutation errors to Sentry
function logMutationError(
  error: any,
  context: {
    flow: string
    email?: string
    qrLookupID?: string
    additionalData?: Record<string, any>
  }
) {
  Sentry.captureException(error, {
    extra: {
      email: context.email,
      qrLookupID: context.qrLookupID,
      originalError: error.message,
      ...context.additionalData,
    },
    tags: {
      section: "account",
      flow: context.flow,
    },
  })
}

function RouteComponent() {
  const { qrLookupID } = Route.useSearch()
  const { t } = useTranslation(["errors", "common"])
  const navigate = useNavigate()

  const form = useForm<UpdatePasswordFormSchema>({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    mode: "onBlur",
  })

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    onError,
  })

  return (
    <Card>
      <Heading level={2}>
        {t("account.updatePassword.header", { ns: "common" })}
      </Heading>
      <Text style={{ marginTop: "var(--space-2)" }}>
        {t("account.updatePassword.prefix", { ns: "common" })}
        &nbsp;
        <strong>{t("account.updatePassword.brand", { ns: "common" })}</strong>
        &nbsp;
        {t("account.updatePassword.suffix", { ns: "common" })}
      </Text>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset disabled={isPending}>
          <Controller<UpdatePasswordFormSchema>
            name="currentPassword"
            control={form.control}
            rules={{ required: requiredRule(t("common.required")) }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>
                  {t("account.updatePassword.currentPassword", { ns: "common" })}
                </Label>
                <TextField.Password
                  innerRef={ref}
                  aria-label={t("account.updatePassword.currentPassword", {
                    ns: "common",
                  })}
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller<UpdatePasswordFormSchema>
            name="newPassword"
            control={form.control}
            rules={{
              required: requiredRule(t("common.required", { ns: "errors" })),
              ...passwordValidationRule(t),
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>
                  {t("account.updatePassword.newPassword", { ns: "common" })}
                </Label>
                <TextField.Password
                  innerRef={ref}
                  aria-label={t("account.updatePassword.newPassword", {
                    ns: "common",
                  })}
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller<UpdatePasswordFormSchema>
            name="confirmPassword"
            control={form.control}
            rules={{
              required: requiredRule(t("common.required", { ns: "errors" })),
              validate: (value) => {
                const newPassword = form.getValues("newPassword")
                return (
                  value === newPassword ||
                  t("common.passwordMismatch", { ns: "errors" })
                )
              },
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>
                  {t("account.updatePassword.confirmPassword", { ns: "common" })}
                </Label>
                <TextField.Password
                  innerRef={ref}
                  aria-label={t("account.updatePassword.confirmPassword", {
                    ns: "common",
                  })}
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          {form.formState.errors.root && (
            <FieldError className="center">
              {form.formState.errors.root.message}
            </FieldError>
          )}
          <Button buttonType="primary" type="submit" isPending={isPending}>
            {t("account.updatePassword.save", { ns: "common" })}
          </Button>
        </fieldset>
      </form>
    </Card>
  )

  function onSubmit(data: UpdatePasswordFormSchema) {
    form.clearErrors("root")
    mutate(data)
  }

  async function mutationFn(data: UpdatePasswordFormSchema) {
    return api.auth.updatePassword({
      oldPassword: data.currentPassword,
      newPassword: data.newPassword,
    })
  }

  function onSuccess() {
    // Navigate back to account page
    navigate({
      to: "/account",
      search: { qrLookupID },
    })
  }

  function onError(err: Error, variables: UpdatePasswordFormSchema) {
    logMutationError(err, {
      flow: "update-password",
      qrLookupID,
      additionalData: {
        hasCurrentPassword: !!variables.currentPassword,
        hasNewPassword: !!variables.newPassword,
        hasConfirmPassword: !!variables.confirmPassword,
      },
    })
    handleAuthError(err, form, t)
  }
}
